/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, {
  forwardRef,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import {
  EditingState,
  SortingState,
  IntegratedSorting,
  DataTypeProvider,
  SearchState,
  IntegratedFiltering,
  PagingState,
  IntegratedPaging,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  TableHeaderRow,
  TableEditColumn,
  Toolbar,
  SearchPanel,
  DragDropProvider,
  Table,
  TableColumnReordering,
  TableColumnResizing,
  TableColumnVisibility,
  ColumnChooser,
  PagingPanel,
} from '@devexpress/dx-react-grid-material-ui';
import { Command, PopupEditing } from '../../Shared';
import { useEmployees, useTemplate } from '../../hooks';
import { downloadCSVFile, getRowId, userroles } from '../../common';
import { PopupRentitem } from '../../pubups';
import {
  currencyFormatter,
  dateFormatter,
  photoFormatter,
} from '../../Shared/colorFormat';
import { AlertLocal, SearchTable } from '../../components';
import { errorAlert, errorDeleteAlert } from '../../Shared/helpers';
import { Box, Typography } from '@material-ui/core';
import { TableComponent } from '../../Shared/TableComponent';
import ImportBtn from '../../common/ImportBtn';
import PopupItemsImport from '../../pubups/PopupItemsImport';
import { getColumns } from '../../common/columns';
import ExportBtn from '../../common/ExportBtn';
import useRentitems from '../../hooks/useRentitems';
import { GlobalContext } from '../../contexts';
import { GContextTypes } from '../../types';

const Rentitems = forwardRef((props: any, ref: any) => {
  const { isRTL, words, theme } = props;
  const [openImport, setOpenImport] = useState(false);
  const [alrt, setAlrt] = useState({ show: false, msg: '', type: undefined });
  const { tempoptions } = useTemplate();
  const { employeesfull } = useEmployees();
  const col = getColumns({ isRTL, words });

  const {
    store: { user },
  }: GContextTypes = useContext(GlobalContext);

  // Check if user can edit packages
  const currentEmployee = employeesfull?.find(
    (emp: any) => emp._id === user?.employeeId,
  );
  const canEditPackages =
    user?.isSuperAdmin ||
    user?.isBranchAdmin ||
    user?.isBranchsAdmin ||
    currentEmployee?.canEditPackages === true;

  const [columns] = useState(
    [
      { name: 'photo', title: ' ' },
      tempoptions?.barcode && { name: 'barcode', title: words.code },
      col.name,
      { name: 'price', title: words.theprice },
      { name: 'unit', title: words.unit },
      { name: 'qty', title: words.qty },
      col.category,
      {
        name: 'purchaseDate',
        title: isRTL ? 'تاريخ الشراء' : 'Purchase Date',
        type: 'date',
      },
      { name: 'rentitemcost', title: isRTL ? 'قيمة البند' : 'Rent Item Cost' },
      {
        name: 'yeardepreciation',
        title: isRTL ? 'اهلاك سنوي' : 'Year Depreciation',
      },
      {
        name: 'rentdepreciation',
        title: isRTL ? 'اهلاك التأجير' : 'Rent Depreciation',
      },
      { name: 'desc', title: words.description },
    ].filter((x) => x),
  );

  const [tableColumnExtensions]: any = useState([
    { columnName: 'photo', width: 75 },
    { columnName: 'barcode', width: 150 },
    { columnName: col.name.name, width: 250 },
    { columnName: 'price', width: 150 },
    { columnName: 'unit', width: 150 },
    { columnName: 'qty', width: 150 },
    { columnName: col.category.name, width: 200 },
    { columnName: 'purchaseDate', width: 150 },
    { columnName: 'rentitemcost', width: 150 },
    { columnName: 'yeardepreciation', width: 150 },
    { columnName: 'rentdepreciation', width: 150 },
    { columnName: 'desc', width: 250 },
  ]);

  const {
    rentitems,
    addRentitem,
    addMultiRentitems,
    editRentitem,
    removeRentitem,
    refreshrentitem,
  } = useRentitems();
  const commitChanges = async ({ deleted }) => {
    if (deleted) {
      const _id = deleted[0];
      const res = await removeRentitem({ variables: { _id } });
      if (res?.data?.deleteItem?.ok === false) {
        if (res?.data?.deleteItem?.error.includes('related')) {
          await errorDeleteAlert(setAlrt, isRTL);
        } else {
          await errorAlert(setAlrt, isRTL);
        }
      }
    }
  };
  useImperativeHandle(ref, () => ({
    refresh() {
      if (refreshrentitem) {
        refreshrentitem();
      }
    },
  }));

  const downloadCSVSubmit = () => {
    const colnames = columns?.map((col: any) => col?.name);
    downloadCSVFile(rentitems, 'rentitems', colnames);
  };

  return (
    <>
      {canEditPackages && (
        <Box
          style={{
            position: 'absolute',
            top: 8,
            left: isRTL ? 400 : undefined,
            right: isRTL ? undefined : 400,
            display: 'flex',
          }}
        >
          <ImportBtn open={() => setOpenImport(true)} isRTL={isRTL}></ImportBtn>
          <ExportBtn open={downloadCSVSubmit} isRTL={isRTL}></ExportBtn>
        </Box>
      )}

      <Grid rows={rentitems} columns={columns} getRowId={getRowId}>
        <SortingState />
        <EditingState onCommitChanges={commitChanges} />
        <SearchState />
        <PagingState defaultCurrentPage={0} defaultPageSize={20} />
        <IntegratedSorting />
        <IntegratedFiltering />
        <IntegratedPaging />
        <DragDropProvider />
        <Table
          messages={{
            noData: isRTL ? 'لا يوجد بيانات' : 'no data',
          }}
          tableComponent={TableComponent}
          rowComponent={(props: any) => (
            <Table.Row {...props} style={{ height: 68 }}></Table.Row>
          )}
          columnExtensions={tableColumnExtensions}
        />
        <TableColumnReordering
          defaultOrder={[
            'photo',
            col.name.name,
            'price',
            'qty',
            'unit',
            col.category.name,
            'purchaseDate',
            'rentitemcost',
            'yeardepreciation',
            'rentdepreciation',
            'desc',
          ]}
        />
        <TableColumnResizing defaultColumnWidths={tableColumnExtensions} />
        <TableHeaderRow
          showSortingControls
          titleComponent={({ children }) => {
            return (
              <Typography style={{ fontSize: 14, fontWeight: 'bold' }}>
                {children}
              </Typography>
            );
          }}
        />
        <TableColumnVisibility defaultHiddenColumnNames={[]} />
        <DataTypeProvider
          for={['photo']}
          formatterComponent={photoFormatter}
        ></DataTypeProvider>
        <DataTypeProvider
          for={['purchaseDate']}
          formatterComponent={dateFormatter}
        ></DataTypeProvider>
        <DataTypeProvider
          for={[
            'price',
            'rentitemcost',
            'yeardepreciation',
            'rentdepreciation',
          ]}
          formatterComponent={currencyFormatter}
        ></DataTypeProvider>

        <TableEditColumn
          showEditCommand={userroles?.adds?.edit && canEditPackages}
          showDeleteCommand={userroles?.adds?.delete && canEditPackages}
          showAddCommand={userroles?.adds?.add && canEditPackages}
          commandComponent={Command}
        ></TableEditColumn>
        <Toolbar />
        <ColumnChooser />
        <PagingPanel
          pageSizes={
            tempoptions?.pageall ? [5, 10, 20, 50, 0] : [5, 10, 20, 50]
          }
        />

        <SearchPanel
          inputComponent={(props: any) => {
            return <SearchTable isRTL={isRTL} {...props}></SearchTable>;
          }}
        />
        <PopupEditing
          theme={theme}
          addAction={addRentitem}
          editAction={editRentitem}
        >
          <PopupRentitem></PopupRentitem>
        </PopupEditing>
      </Grid>
      {alrt.show && (
        <AlertLocal
          isRTL={isRTL}
          type={alrt?.type}
          msg={alrt?.msg}
          top
        ></AlertLocal>
      )}
      <PopupItemsImport
        open={openImport}
        onClose={() => setOpenImport(false)}
        addMultiItems={addMultiRentitems}
        isRTL={isRTL}
        theme={theme}
        words={words}
        itemType={4}
        filename="rentitems"
      ></PopupItemsImport>
    </>
  );
});
export default Rentitems;
